"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { useAuth } from "./AuthProvider";
import Avatar from "./Avatar";
import AuthModal from "./AuthModal";
import SettingsModal from "./SettingsModal";

interface UserMenuProps {
  variant?: "floating" | "rail";
}

export default function UserMenu({ variant = "floating" }: UserMenuProps) {
  const { user, loading, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);
  const [authMode, setAuthMode] = useState<"login" | "register" | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  if (loading) return null;
  if (pathname?.startsWith("/admin")) return null;

  const isRail = variant === "rail";

  // On the home page the AppShell / MobileAppShell render their own rail-variant
  // UserMenu inside the sidebar / header, so the global floating instance from
  // layout.tsx should not render.
  if (!isRail && pathname === "/") return null;

  if (!user) {
    return (
      <>
        <div className={isRail
          ? "flex items-center gap-2 rounded-full border border-sky-900/60 bg-white/[0.06] px-3 py-1.5 text-[13px] font-medium text-sky-200 whitespace-nowrap flex-shrink-0"
          : "flex items-center gap-3 rounded-full border border-sky-900/60 bg-ocean-900/90 px-[18px] py-[9px] text-lg font-medium text-sky-200 shadow-lg backdrop-blur"
        }>
          <button onClick={() => setAuthMode("login")} className="hover:text-foam transition-colors">
            Sign in
          </button>
          <span className="text-sky-900/60">·</span>
          <button onClick={() => setAuthMode("register")} className="hover:text-foam transition-colors">
            Register
          </button>
        </div>
        {authMode && <AuthModal initialMode={authMode} onClose={() => setAuthMode(null)} />}
      </>
    );
  }

  async function handleSignOut() {
    setOpen(false);
    await signOut();
    router.refresh();
  }

  const label = user.display_name || `@${user.username}`;

  return (
    <>
      <div ref={menuRef} className={isRail
        ? "relative flex flex-col items-end whitespace-nowrap flex-shrink-0"
        : "relative flex flex-col items-end"
      }>
        <button
          onClick={() => setOpen((o) => !o)}
          className={isRail
            ? "flex items-center gap-2 rounded-full border border-sky-900/60 bg-white/[0.06] py-0.5 pl-0.5 pr-3 text-[13px] font-semibold text-foam hover:bg-white/[0.1] transition-colors"
            : "flex items-center gap-3 rounded-full border border-sky-900/60 bg-ocean-900/90 py-1 pl-1 pr-4 text-lg font-semibold text-foam shadow-lg backdrop-blur hover:bg-ocean-800"
          }
        >
          <Avatar url={user.avatar_url} name={label} size={isRail ? 36 : 42} />
          <span className={isRail ? "max-w-[110px] truncate" : ""}>{label}</span>
        </button>
        {open && (
          <div className={isRail
            ? "absolute top-full right-0 z-50 mt-1 flex w-44 flex-col overflow-hidden rounded-lg border border-sky-900/60 bg-ocean-900/95 text-xs text-sky-200 shadow-lg backdrop-blur"
            : "mt-2 flex w-44 flex-col overflow-hidden rounded-lg border border-sky-900/60 bg-ocean-900/95 text-xs text-sky-200 shadow-lg backdrop-blur"
          }>
            <Link
              href={`/?panel=profile&u=${encodeURIComponent(user.username)}`}
              onClick={() => setOpen(false)}
              className="px-3 py-2 hover:bg-ocean-800 hover:text-foam"
            >
              Profile
            </Link>
            {user.is_admin && (
              <Link
                href="/admin"
                onClick={() => setOpen(false)}
                className="px-3 py-2 text-predict hover:bg-ocean-800"
              >
                Admin Panel
              </Link>
            )}
            <button
              onClick={() => {
                setOpen(false);
                setSettingsOpen(true);
              }}
              className="px-3 py-2 text-left hover:bg-ocean-800 hover:text-foam"
            >
              Settings
            </button>
            <button
              onClick={handleSignOut}
              className="border-t border-sky-900/60 px-3 py-2 text-left text-red-400 hover:bg-ocean-800"
            >
              Sign out
            </button>
          </div>
        )}
      </div>
      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}
    </>
  );
}
